import { useState, useRef, useEffect } from 'react';
import { Printer, Loader2, Trash2, ArrowLeft, User, Building2, Users, Clock, CheckCircle2, AlertCircle, ExternalLink, Hash, Calendar, CreditCard } from 'lucide-react';
import type { PrintableRef, Currency } from '@/types';
import PrintableDocument from '@/components/PrintableDocument';
import InvoicesService from '@/api/invoices.service';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import FormModal from '@/components/FormModal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { hasVatTax, calculateItemVat } from '@/utils/tax-utils'
import { format, parseISO } from 'date-fns';
import { CustomerTaxInfo } from '@/components/tax/CustomerTaxInfo';
import TaxesService from '@/api/taxes.service';

// Update the Customer type to match the schema
type Customer = {
  id: number;
  publicId: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  emailAddress: string;
  street: string;
  city: string;
  state: string;
  zipCode?: string; // Optional
  country: string;
  companyName?: string; // Optional
};

export default function Invoice({ invoiceId }: { invoiceId: string }) {
  const [logo, setLogo] = useState<string>();
  const [signature, setSignature] = useState<string>();
  const [currency, setCurrency] = useState<Currency>('USD');
  const [items, setItems] = useState([{ description: '', quantity: 1, unitPrice: 0 }]);
  const [status, setStatus] = useState('pending');
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('');
  const [companyDetails, setCompanyDetails] = useState({
    name: '',
    address: '',
    city: '',
    country: '',
    emailAddress: '',
    phoneNumber: '',
    logoImg: '',
    publicId: ''
  });
  const [customerDetails, setCustomerDetails] = useState<Customer>({
    id: 0,
    publicId: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    emailAddress: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    companyName: ''
  });
  const [userDetails, setUserDetails] = useState({ 
    publicId: '',
    firstName: '',
    lastName: ''
  });
  const [taxes, setTaxes] = useState([{ taxName: '', taxRate: 0, taxAmount: 0 }]);
  const [issueDate, setIssueDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const printableRef = useRef<PrintableRef>(null);
  
  const [isLoading, setIsLoading] = useState(false);
  const [isInclusive, setIsInclusive] = useState(false);
  const [customerTaxInfos, setCustomerTaxInfos] = useState<any[]>([]);
  const [businessTaxInfos, setBusinessTaxInfos] = useState<any[]>([]);

  const { toast } = useToast();
  const navigate = useNavigate();
  const showToastWithTimeout = (toastType: string, title: string, description: string, duration: number) => {
    toast({
      title: title,
      description: description,
      variant: toastType === "error" ? "destructive" : "default",
      duration: duration,
    });
  };

  useEffect(() => {
    const fetchInvoice = async () => {
      setIsLoading(true);
      try {
        const response = await InvoicesService.getInvoiceById(parseInt(invoiceId));
        const { data } = response;
        console.log("getInvoiceById data", data);
        if (data.result === 'success') {
          const invoice = data.invoice;
          setCompanyDetails(invoice.business);
          setCustomerDetails(invoice.customer);
          setUserDetails(invoice.user);
          setItems(invoice.invoiceItems);
          setTaxes(invoice.invoiceTaxes);
          setNotes(invoice.notes);
          setTerms(invoice.terms);
          setSignature(invoice.signature);
          setCurrency(invoice.currency);
          setStatus(invoice.status);
          setInvoiceNumber(invoice.invoiceNumber);
          setIssueDate(invoice.issueDate);
          setDueDate(invoice.dueDate);
          setIsInclusive(data.isInclusive || false);
          
          // Fetch customer tax information
          if (invoice.customer?.id) {
            try {
              const taxResponse = await TaxesService.getCustomerTaxInfos(invoice.customer.id);
              if (taxResponse.data.result === 'success') {
                setCustomerTaxInfos(taxResponse.data.customerTaxInfos || []);
              }
            } catch (error) {
              console.error('Failed to fetch customer tax info:', error);
            }
          }
          
          // Fetch business tax information
          if (invoice.business?.id) {
            try {
              const businessTaxResponse = await TaxesService.getBusinessTaxInfos(invoice.business.id);
              if (businessTaxResponse.data.result === 'success') {
                setBusinessTaxInfos(businessTaxResponse.data.businessTaxInfos || []);
              }
            } catch (error) {
              console.error('Failed to fetch business tax info:', error);
            }
          }
        } else {
          showToastWithTimeout("error", "Failed to fetch invoice", data.message, 3000);
        }
      } catch (error) {
        console.error(error);
        showToastWithTimeout("error", "Failed to fetch invoice", "An error occurred while fetching the invoice.", 3000);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoice();
  }, [invoiceId]);

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    if (selectedBusiness) {
      setCompanyDetails({
        name: selectedBusiness.name,
        address: selectedBusiness.address,
        city: selectedBusiness.city,
        country: selectedBusiness.country,
        emailAddress: selectedBusiness.emailAddress,
        phoneNumber: selectedBusiness.phoneNumber,
        logoImg: selectedBusiness.logoImg,
        publicId: selectedBusiness.publicId
      });
      setLogo(selectedBusiness.logoImg);
    }
  }, [selectedBusiness]);

  const handlePrint = () => {
    if (printableRef.current) {
      printableRef.current.print();
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const response = await InvoicesService.deleteInvoice(parseInt(invoiceId));
      if (response.data.result === 'success') {
        showToastWithTimeout("default", "Invoice deleted", "Invoice has been successfully deleted", 3000);
        setTimeout(() => {
          navigate('/invoices');
        }, 2000);
      } else {
        showToastWithTimeout("error", "Delete failed", response.data.message, 3000);
      }
    } catch (error) {
      console.error(error);
      showToastWithTimeout("error", "Delete failed", "An error occurred while deleting the invoice", 3000);
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  };

  const calculateTotalTax = () => {
    return taxes.reduce((sum, tax) => sum + tax.taxAmount, 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTotalTax();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };
  
  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch (e) {
      return dateString;
    }
  };

  const getStatusBadge = () => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-500/10 text-green-700 dark:bg-green-900/20 dark:text-green-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Paid</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100 flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</Badge>;
      case 'overdue':
        return <Badge className="bg-red-500/10 text-red-700 dark:bg-red-900/20 dark:text-red-400 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Overdue</Badge>;
      case 'partially_paid':
        return <Badge className="bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Partially Paid</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // Check if taxes include VAT
  console.log("taxes", taxes);
  const hasVat = hasVatTax(taxes);

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            size="sm"
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft size={16} />
            Back
          </Button>
          <h1 className="text-2xl font-semibold">Invoice {invoiceNumber}</h1>
          {invoiceNumber && (
            <Badge variant="outline" className="ml-2 text-sm">
              #{invoiceNumber}
            </Badge>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setIsDeleteModalOpen(true)}
            variant="destructive"
            size="sm"
            className="flex items-center gap-1"
            disabled={isDeleting || isLoading}
          >
            {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 size={16} />}
            Delete
          </Button>
          <Button
            onClick={handlePrint}
            variant="outline"
            size="sm"
            className="flex items-center gap-1"
            disabled={!invoiceNumber || isLoading}
          >
            <Printer size={16} />
            Print
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-2">
        <Button 
          onClick={() => navigate(`/user/${userDetails.publicId}`)}
          variant="outline"
          size="sm"
          className="flex items-center gap-1"
        >
          <User size={16} />
          View User
        </Button>
        <Button 
          onClick={() => navigate(`/business/${companyDetails.publicId}`)}
          variant="outline"
          size="sm"
          className="flex items-center gap-1"
        >
          <Building2 size={16} />
          View Business
        </Button>
        <Button
          onClick={() => navigate(`/customer/${customerDetails.publicId}`)}
          variant="outline"
          size="sm"
          className="flex items-center gap-1"
        >
          <Users size={16} />
          View Customer
        </Button>
      </div>

      <FormModal
        open={isDeleteModalOpen}
        setOpen={setIsDeleteModalOpen}
        title="Delete Invoice"
      >
        <div className="space-y-4">
          <p className="text-muted-foreground">Are you sure you want to delete this invoice? This action cannot be undone.</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Invoice
            </Button>
          </div>
        </div>
      </FormModal>
  
      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : 
        companyDetails && ( 
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row gap-6">
            <Card className="flex-1 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                  <Hash size={14} />
                  Invoice Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Invoice Number</p>
                      <p className="font-medium">{invoiceNumber}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Status</p>
                      {getStatusBadge()}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Issue Date</p>
                      <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-muted-foreground" />
                        <p>{formatDate(issueDate)}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Due Date</p>
                      <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-muted-foreground" />
                        <p>{formatDate(dueDate)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="flex-1 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                  <CreditCard size={14} />
                  Payment Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Total Amount</p>
                    <p className="text-2xl font-semibold">{formatCurrency(calculateTotal())}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Currency</p>
                    <Badge variant="outline">{currency}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="flex flex-col md:flex-row gap-6">
            <Card className="flex-1 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                  <Building2 size={14} />
                  Business Information
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-primary"
                  onClick={() => navigate(`/business/${companyDetails.publicId}`)}
                >
                  <ExternalLink size={12} className="mr-1" />
                  View Business
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {companyDetails.logoImg && (
                    <div className="mb-4">
                      <img 
                        src={companyDetails.logoImg} 
                        alt={companyDetails.name} 
                        className="h-12 object-contain" 
                      />
                    </div>
                  )}
                  <p className="font-medium">{companyDetails.name}</p>
                  <p className="text-sm text-muted-foreground">{companyDetails.address}</p>
                  <p className="text-sm text-muted-foreground">{companyDetails.city}, {companyDetails.country}</p>
                  <Separator className="my-2" />
                  <div className="text-sm">
                    <p className="text-muted-foreground">{companyDetails.emailAddress}</p>
                    <p className="text-sm text-muted-foreground">{companyDetails.phoneNumber}</p>
                  </div>
                  
                  {/* Business Tax Information */}
                  {businessTaxInfos.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <p className="text-sm font-medium text-muted-foreground mb-2">Business Tax Information</p>
                      <div className="space-y-2">
                        {businessTaxInfos.map((taxInfo, index) => (
                          <div key={index} className="flex items-center text-sm">
                            <span className="font-medium mr-2">{taxInfo.taxType.name}:</span>
                            <span className="text-muted-foreground">{taxInfo.taxIdentifier}</span>
                            {taxInfo.isVerified && (
                              <span className="ml-2 px-2 py-0.5 text-xs bg-green-500/10 text-green-700 rounded-full">
                                Verified
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            <Card className="flex-1 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                  <User size={14} />
                  Customer Information
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-primary"
                  onClick={() => navigate(`/customer/${customerDetails.publicId}`)}
                >
                  <ExternalLink size={12} className="mr-1" />
                  View Customer
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <p className="font-medium">
                    {customerDetails.firstName} {customerDetails.lastName}
                    {customerDetails.companyName && (
                      <span className="block text-sm text-muted-foreground mt-1">
                        {customerDetails.companyName}
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground">{customerDetails.street}</p>
                  <p className="text-sm text-muted-foreground">
                    {customerDetails.city}, {customerDetails.state} {customerDetails.zipCode}
                  </p>
                  <p className="text-sm text-muted-foreground">{customerDetails.country}</p>
                  <Separator className="my-2" />
                  <div className="text-sm">
                    <p className="text-muted-foreground">{customerDetails.emailAddress}</p>
                    <p className="text-sm text-muted-foreground">{customerDetails.phoneNumber}</p>
                  </div>
                  
                  {/* Add Customer Tax Information */}
                  <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                    <p className="text-sm font-medium text-muted-foreground mb-2">Tax Information</p>
                    <CustomerTaxInfo 
                      customerId={customerDetails.id}
                      readonly
                      minimal
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">Description</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground">Quantity</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground">Unit Price</th>
                      {hasVat && (
                        <>
                          <th className="text-right py-3 px-4 font-medium text-muted-foreground">Total Excl. VAT</th>
                          <th className="text-right py-3 px-4 font-medium text-muted-foreground">VAT Amount</th>
                          <th className="text-right py-3 px-4 font-medium text-muted-foreground">Total Incl. VAT</th>
                        </>
                      )}
                      {!hasVat && (
                        <th className="text-right py-3 px-4 font-medium text-muted-foreground">Amount</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => {
                      const vatCalc = hasVat 
                        ? calculateItemVat(item, taxes, isInclusive)
                        : null;
                      
                      return (
                        <tr key={index} className={cn("border-b", index === items.length - 1 && "border-b-0")}>
                          <td className="py-3 px-4">{item.description}</td>
                          <td className="py-3 px-4 text-right">{item.quantity}</td>
                          <td className="py-3 px-4 text-right">{formatCurrency(item.unitPrice)}</td>
                          {hasVat ? (
                            <>
                              <td className="py-3 px-4 text-right">{formatCurrency(vatCalc?.totalExclVat || 0)}</td>
                              <td className="py-3 px-4 text-right">{formatCurrency(vatCalc?.vatAmount || 0)}</td>
                              <td className="py-3 px-4 text-right">{formatCurrency(vatCalc?.totalInclVat || 0)}</td>
                            </>
                          ) : (
                            <td className="py-3 px-4 text-right">{formatCurrency(item.quantity * item.unitPrice)}</td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 flex justify-end">
                <div className="w-full max-w-xs space-y-2">
                  <div className="flex justify-between py-2">
                    <span className="text-muted-foreground">Subtotal:</span>
                    <span>{formatCurrency(calculateSubtotal())}</span>
                  </div>
                  
                  {taxes.map((tax, index) => (
                    <div key={index} className="flex justify-between py-2">
                      <span className="text-muted-foreground">{tax.taxName} ({tax.taxRate}%):</span>
                      <span>{formatCurrency(tax.taxAmount)}</span>
                    </div>
                  ))}
                  
                  <Separator />
                  
                  <div className="flex justify-between py-2 font-medium text-lg">
                    <span>Total:</span>
                    <span>{formatCurrency(calculateTotal())}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {(notes || terms) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Additional Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {notes && (
                  <div>
                    <h3 className="font-medium mb-2">Notes</h3>
                    <p className="text-muted-foreground text-sm whitespace-pre-line">{notes}</p>
                  </div>
                )}
                
                {terms && (
                  <div>
                    <h3 className="font-medium mb-2">Terms and Conditions</h3>
                    <p className="text-muted-foreground text-sm whitespace-pre-line">{terms}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {signature && (
            <Card className="shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Signature
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border rounded-md p-4 bg-muted/20">
                  <img
                    src={signature}
                    alt="Signature"
                    className="max-h-[100px]"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {userDetails && (
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-muted-foreground"
                onClick={() => navigate(`/user/${userDetails.publicId}`)}
              >
                <User size={12} className="mr-1" />
                Created by {userDetails.firstName} {userDetails.lastName}
              </Button>
            </div>
          )}

          <div className="hidden">
            <PrintableDocument
              ref={printableRef}
              document={{
                id: invoiceId,
                number: invoiceNumber,
                type: 'invoice',
                date: new Date().toISOString().split('T')[0],
                currency,
                items,
                status,
                companyDetails,
                customerDetails: {
                  companyName: customerDetails.companyName,
                  name: `${customerDetails.firstName} ${customerDetails.lastName}`,
                  address: `${customerDetails.street}, ${customerDetails.city}, ${customerDetails.state}, ${customerDetails.country}`,
                  emailAddress: customerDetails.emailAddress,
                  phoneNumber: customerDetails.phoneNumber,
                },
                notes,
                terms,
                logo,
                signature,
                taxes: taxes.map(tax => ({
                  taxName: tax.taxName,
                  taxRate: tax.taxRate,
                  taxAmount: tax.taxAmount,
                })),
                issueDate: issueDate.split('-').reverse().join('/'),
                dueDate: dueDate.split('-').reverse().join('/'),
                isInclusive,
                customerTaxInfos,
                businessTaxInfos,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}